import { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, Search, X, Users, Layers, BookOpen, Scissors, Package } from 'lucide-react'
import { operatorService } from '../services/operatorService'

const STATIONS = [
  { id: 'susun', label: 'QC Susun', icon: Layers, color: 'amber' },
  { id: 'binding', label: 'QC Binding', icon: BookOpen, color: 'emerald' },
  { id: 'potong', label: 'QC Potong Sisir', icon: Scissors, color: 'cyan' },
  { id: 'laminasi', label: 'QC Laminasi', icon: Package, color: 'pink' },
]

function OperatorsPage() {
  const [operators, setOperators] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStation, setFilterStation] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingOperator, setEditingOperator] = useState(null)

  useEffect(() => {
    fetchOperators()
  }, [filterStation])

  const fetchOperators = async () => {
    setLoading(true)
    try {
      const params = { status: 'all' }
      if (filterStation) params.station = filterStation
      const res = await operatorService.getAll(params)
      if (res.success) {
        setOperators(res.data.operators || [])
      }
    } catch (error) {
      console.error('Error fetching operators:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (operator) => {
    if (!confirm(`Hapus operator "${operator.name}"?`)) return
    try {
      await operatorService.delete(operator.id)
      fetchOperators()
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message))
    }
  }

  const handleEdit = (operator) => {
    setEditingOperator(operator)
    setShowModal(true)
  }

  const handleAdd = () => {
    setEditingOperator(null)
    setShowModal(true)
  }

  const handleModalSuccess = () => {
    setShowModal(false)
    setEditingOperator(null)
    fetchOperators()
  }

  const filteredOperators = operators.filter(op =>
    op.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Group by station
  const groupedOperators = STATIONS.map(station => ({
    ...station,
    operators: filteredOperators.filter(op => op.station === station.id)
  }))

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-xl font-bold text-gray-800">Master Operator</h1>
            <p className="text-sm text-gray-500">Kelola daftar operator per stasiun</p>
          </div>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Tambah Operator
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Cari nama operator..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
          />
        </div>
        <select
          value={filterStation}
          onChange={e => setFilterStation(e.target.value)}
          className="px-4 py-2 border rounded-lg text-sm"
        >
          <option value="">Semua Stasiun</option>
          {STATIONS.map(s => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {STATIONS.map(station => {
          const count = operators.filter(op => op.station === station.id && op.status === 'active').length
          const Icon = station.icon
          const colorClasses = {
            amber: 'bg-amber-50 border-amber-200 text-amber-700',
            emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
            cyan: 'bg-cyan-50 border-cyan-200 text-cyan-700',
            pink: 'bg-pink-50 border-pink-200 text-pink-700',
          }
          return (
            <div key={station.id} className={`p-3 rounded-lg border ${colorClasses[station.color]}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{station.label}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{count}</p>
              <p className="text-xs opacity-70">operator aktif</p>
            </div>
          )
        })}
      </div>

      {/* List by Station */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : filterStation ? (
        // Single station view
        <OperatorList
          operators={filteredOperators}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ) : (
        // Grouped view
        <div className="space-y-4">
          {groupedOperators.map(group => {
            if (group.operators.length === 0) return null
            const Icon = group.icon
            const colorClasses = {
              amber: 'border-l-amber-500 bg-amber-50',
              emerald: 'border-l-emerald-500 bg-emerald-50',
              cyan: 'border-l-cyan-500 bg-cyan-50',
              pink: 'border-l-pink-500 bg-pink-50',
            }
            const headerColors = {
              amber: 'text-amber-700',
              emerald: 'text-emerald-700',
              cyan: 'text-cyan-700',
              pink: 'text-pink-700',
            }
            return (
              <div key={group.id} className={`rounded-lg border-l-4 ${colorClasses[group.color]} overflow-hidden`}>
                <div className={`p-3 flex items-center gap-2 ${headerColors[group.color]}`}>
                  <Icon className="w-5 h-5" />
                  <span className="font-bold">{group.label}</span>
                  <span className="text-sm opacity-70">({group.operators.length} operator)</span>
                </div>
                <div className="bg-white">
                  <OperatorList
                    operators={group.operators}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    compact
                  />
                </div>
              </div>
            )
          })}
          {filteredOperators.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Belum ada operator</p>
              <button onClick={handleAdd} className="mt-2 text-blue-600 hover:underline">
                + Tambah Operator Baru
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <OperatorModal
          operator={editingOperator}
          onClose={() => { setShowModal(false); setEditingOperator(null) }}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  )
}

// ============================================
// OPERATOR LIST
// ============================================
function OperatorList({ operators, onEdit, onDelete, compact = false }) {
  if (operators.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 text-sm">
        Tidak ada operator
      </div>
    )
  }

  return (
    <div className={compact ? '' : 'bg-white rounded-lg shadow-sm'}>
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nama</th>
            {!compact && <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stasiun</th>}
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telepon</th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Aksi</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {operators.map(op => (
            <tr key={op.id} className="hover:bg-gray-50">
              <td className="px-4 py-2">
                <span className="font-medium text-gray-800">{op.name}</span>
              </td>
              {!compact && (
                <td className="px-4 py-2">
                  <StationBadge station={op.station} />
                </td>
              )}
              <td className="px-4 py-2 text-sm text-gray-600">{op.phone || '-'}</td>
              <td className="px-4 py-2">
                <span className={`px-2 py-0.5 text-xs rounded ${
                  op.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {op.status === 'active' ? 'Aktif' : 'Nonaktif'}
                </span>
              </td>
              <td className="px-4 py-2 text-right">
                <button
                  onClick={() => onEdit(op)}
                  className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                  title="Edit"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDelete(op)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded ml-1"
                  title="Hapus"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ============================================
// STATION BADGE
// ============================================
function StationBadge({ station }) {
  const config = STATIONS.find(s => s.id === station)
  if (!config) return <span className="text-gray-500">{station}</span>

  const colorClasses = {
    amber: 'bg-amber-100 text-amber-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    pink: 'bg-pink-100 text-pink-700',
  }

  return (
    <span className={`px-2 py-0.5 text-xs rounded ${colorClasses[config.color]}`}>
      {config.label}
    </span>
  )
}

// ============================================
// OPERATOR MODAL
// ============================================
function OperatorModal({ operator, onClose, onSuccess }) {
  const [name, setName] = useState(operator?.name || '')
  const [station, setStation] = useState(operator?.station || 'susun')
  const [phone, setPhone] = useState(operator?.phone || '')
  const [status, setStatus] = useState(operator?.status || 'active')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) return alert('Nama operator harus diisi')
    setSaving(true)
    try {
      if (operator) {
        await operatorService.update(operator.id, { name, station, phone, status })
      } else {
        await operatorService.create({ name, station, phone })
      }
      onSuccess()
    } catch (error) {
      alert('Error: ' + (error.response?.data?.message || error.message))
    }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-md mx-4 rounded-xl shadow-xl overflow-hidden">
        <div className="p-4 bg-blue-600 text-white flex items-center justify-between">
          <h3 className="font-bold text-lg">
            {operator ? 'Edit Operator' : 'Tambah Operator Baru'}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-blue-700 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nama Operator *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border rounded-lg p-2 text-sm"
              placeholder="Masukkan nama operator"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stasiun *</label>
            <select
              value={station}
              onChange={e => setStation(e.target.value)}
              className="w-full border rounded-lg p-2 text-sm"
            >
              {STATIONS.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">No. Telepon</label>
            <input
              type="text"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="w-full border rounded-lg p-2 text-sm"
              placeholder="Opsional"
            />
          </div>

          {operator && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="w-full border rounded-lg p-2 text-sm"
              >
                <option value="active">Aktif</option>
                <option value="inactive">Nonaktif</option>
              </select>
            </div>
          )}
        </div>

        <div className="p-4 bg-gray-50 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Batal
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium text-white ${
              name.trim() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {saving ? 'Menyimpan...' : 'Simpan'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default OperatorsPage
